import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Sparkles, Copy, ExternalLink, Save, Eye, AtSign,
  Upload, Image as ImageIcon, Gift, ShoppingBag, Trash2, FileText,
  HelpCircle, Camera, X, Search, Loader2, ArrowRight,
} from "lucide-react";
import TutorialVideo from "../TutorialVideo";

/* ════════════════════════════════════════════════════════════════════════
   "Your Mirror Site" tab — every member's personal Faitharama landing page
   editor. Now built as a one-page mini-funnel:

     1. YOUR PHOTO (upload)
     2. HEADLINE + TAGLINE
     3. PRODUCT IMAGE (upload)
     4. FREE GIFT — title + description + email-capture CTA
     5. UPSELL — title + description + price + button URL
     6. TESTIMONY — why I love Faitharama

   They share /m/{handle} and visitors see all of it on one scrolling page.
   ════════════════════════════════════════════════════════════════════════ */

const G    = "#2E9F3A";
const O    = "#F39A1F";
const WINE = "#0F172A";
const NAVY = "#0F172A";
const GOLD = "#2E9F3A";

/* ════════════════════════════════════════════════════════════════════════
   STARTER TEMPLATES — one-click pre-fill for the funnel fields.
   These replace the old Funnel Builder. Member clicks a template, the
   headline/free-gift/upsell sections fill in with Mike-voiced copy. They
   tweak it, drop their photo + product image, save.
   ════════════════════════════════════════════════════════════════════════ */
const STARTER_TEMPLATES = [
  {
    id: "goli",
    name: "GOLI — Apple Cider Vinegar Gummies",
    blurb: "Goli ACV gummies funnel. Example copy is loaded — edit anything you want, then save.",
    accent: "#C0392B",
    preview: "/templates/products/goli.png",
    apply: () => ({
      headline: "Apple Cider Vinegar Without The Burn.",
      tagline: "Two gummies a day. Made from real apples, beetroot and pomegranate. No vinegar shot. No enamel damage.",
      free_gift_title: "FREE: 7-Day ACV Starter Guide",
      free_gift_description: "What apple-cider vinegar actually does, how to use it safely, and the small daily habit my readers swear by. Delivered to your inbox in 60 seconds.",
      free_gift_cta: "Send Me The Guide",
      upsell_title: "Goli Apple Cider Vinegar Gummies",
      upsell_description: "60 gummies per bottle · Vegan · Gluten-free · Real apples, beetroot, pomegranate · No artificial flavors. Free shipping on subscriptions.",
      upsell_price: 19.00,
      upsell_cta_label: "Shop Goli — My Link",
      upsell_cta_url: "https://shopgoli.com/faitharama",
    }),
  },
  {
    id: "book",
    name: "THE BOOK — Mike's Flagship",
    blurb: "Sell the book + free audio + free 90-day course. Highest tier-friendly.",
    accent: "#0F172A",
    preview: "/with-faith/cover.png",
    apply: () => ({
      headline: "What If You Already Have Everything You Need To Change Your Life?",
      tagline: "Mike Boyd's flagship book. Paperback today, audio + course free.",
      free_gift_title: "FREE: First Two Chapters Of Mike's Book",
      free_gift_description: "Read Chapter 1 'God Gave Everybody A Talent' and Chapter 2 'Building A Better You' — free, instant download.",
      free_gift_cta: "Send Me The Chapters",
      upsell_title: "With Faith All Things Are Possible — The Full Book",
      upsell_description: "20 chapters · Plain English · Audiobook FREE · Plus the 90-Day Action Course FREE when you order today. Written for working folks and praying grandmothers, not seminary professors.",
      upsell_price: 19.95,
      upsell_cta_label: "Get The Book + Free Course",
      upsell_cta_url: "https://faitharama.com/with-faith",
    }),
  },
  {
    id: "devotional",
    name: "FREE DEVOTIONAL → $49 Upgrade",
    blurb: "Lead magnet that converts free readers to paid members. Highest-converting funnel.",
    accent: "#2E9F3A",
    preview: "/templates/faith/bible-light.jpg",
    apply: () => ({
      headline: "A 4-Week Devotional For The Hardest Season Of Your Life.",
      tagline: "28 mornings. 8 minutes each. Free, because pain shouldn't have a price tag.",
      free_gift_title: "FREE: 4-Week Devotional (28 Mornings)",
      free_gift_description: "Whatever you're carrying — grief, money trouble, a body that's failing — this devotional was written for that. One scripture, one prayer, one truth. Free, every morning at 6 AM.",
      free_gift_cta: "Send Me Day 1 — Free",
      upsell_title: "Want Everything? Try Pro — 7 Days Free",
      upsell_description: "Unlock the AI Writer, your own Mirror Site, the Email Engine, and 50% commissions on every product. Cancel any time.",
      upsell_price: 49,
      upsell_cta_label: "Start My Pro Plan",
      upsell_cta_url: "https://faitharama.com/pricing",
    }),
  },
  {
    id: "bridge",
    name: "MIRROR BRIDGE — Cold Traffic",
    blurb: "Pre-sells your Mirror Site to cold Facebook/text traffic before they sign up.",
    accent: "#2E9F3A",
    preview: "/courses/aff101-tile-couple-laptop.png",
    apply: () => ({
      headline: "Before You Click Through — Let Me Tell You Why This Is Different.",
      tagline: "Not an MLM. Not a scheme. A wellness platform that pays real people.",
      free_gift_title: "FREE: The 5-Minute Faitharama Welcome",
      free_gift_description: "A short note from me telling you what you're getting (and what you're NOT getting). No spam, no 47-email funnel — just the truth about why I joined.",
      free_gift_cta: "Send Me The Welcome",
      upsell_title: "Ready? Join Faitharama — 7-Day Free Trial",
      upsell_description: "Start free. Get the daily devotional, the walk tracker, Mike's jukebox, and kids' coloring pages — all free, forever. Upgrade only when YOU want to.",
      upsell_price: 0,
      upsell_cta_label: "Start My Free Account",
      upsell_cta_url: "https://faitharama.com/signup",
    }),
  },

  /* ════════ FAITHARAMA AFFILIATE PRODUCT TEMPLATES ════════
     TEMPLATES BELOW ARE INTENTIONALLY BLANK on headlines, taglines, free
     gifts, prices, and stories.  Only the brand name and the affiliate
     URL are pre-filled — those are facts pulled from Mike's affiliate
     dashboards.  Everything else is for Mike to write in his own honest
     words. No AI-invented "I lost 22 pounds" stories, no fake flipbooks,
     no made-up prices. */
  {
    id: "prostavive",
    name: "PROSTAVIVE — Men 50+ Prostate Support",
    blurb: "Prostate supplement starter. Example copy loaded — edit it to sound like you.",
    accent: "#2E9F3A",
    preview: "/templates/products/prostavive.png",
    apply: () => ({
      headline: "Sleep Through The Night Again.",
      tagline: "ProstaVive — a daily supplement made for men over 50 who want their bladder, their sleep, and their dignity back.",
      free_gift_title: "FREE: The 5-Minute Men's Health Check",
      free_gift_description: "A short, honest checklist for men 50 and up. Five questions that tell you what to ask your doctor on the next visit.",
      free_gift_cta: "Send Me The Checklist",
      upsell_title: "ProstaVive Daily Prostate Support",
      upsell_description: "Daily prostate-support supplement designed for men 50+. Take one in the morning. Made in the USA. Free shipping with bundle.",
      upsell_price: 0,
      upsell_cta_label: "Shop ProstaVive — My Link",
      upsell_cta_url: "https://prostavive.com/?ref=MICHAELBOYD",
    }),
  },
  {
    id: "upnourish",
    name: "UPNOURISH — Plant Protein Shake",
    blurb: "Plant-protein shake starter. Example copy loaded — edit anything you want.",
    accent: "#F39A1F",
    preview: "/templates/products/upnourish.png",
    apply: () => ({
      headline: "Real Protein. No Animal. No Junk.",
      tagline: "UpNourish — a plant-based protein shake for folks who want clean fuel without dairy, soy, or that chalky after-taste.",
      free_gift_title: "FREE: 7 Plant-Protein Breakfast Ideas",
      free_gift_description: "Seven 5-minute breakfasts that use one scoop of plant protein. Easy on the stomach. Heavy on the energy.",
      free_gift_cta: "Send Me The Breakfast Plan",
      upsell_title: "UpNourish Plant Protein Shake",
      upsell_description: "Plant-based protein with vitamins and minerals. Dairy-free. Soy-free. Mixes clean in water or milk-alternative.",
      upsell_price: 0,
      upsell_cta_label: "Shop UpNourish — My Link",
      upsell_cta_url: "https://upnourishbrand.com/michael345",
    }),
  },
  {
    id: "wellfit",
    name: "WELLFIT TREADMILL — Folding Home Walker",
    blurb: "Folding walking treadmill starter. Example copy loaded.",
    accent: "#0F172A",
    preview: "/templates/products/wellfit.png",
    apply: () => ({
      headline: "10,000 Steps Without Leaving The House.",
      tagline: "WellFit folds flat under the couch and rolls out when you're ready. No bulky gym equipment. No gym fees.",
      free_gift_title: "FREE: The 10-Minutes-A-Day Walk Plan",
      free_gift_description: "A two-week starter walking plan written for folks who haven't exercised in a while. Print it. Tape it to the fridge.",
      free_gift_cta: "Send Me The Walk Plan",
      upsell_title: "WellFit Folding Home Treadmill",
      upsell_description: "Folding walking treadmill that slides under the couch. Quiet motor. Remote control. Free shipping in the US.",
      upsell_price: 0,
      upsell_cta_label: "Shop WellFit — My Link",
      upsell_cta_url: "https://wellfittreadmill.com/?ref=MICHAELBOYD",
    }),
  },
  {
    id: "slim-kitchen",
    name: "SIMPLE SLIM KITCHEN — Recipe Plan",
    blurb: "Healthy recipe plan starter — digital download.",
    accent: "#C0392B",
    preview: "/templates/products/slim-kitchen.png",
    apply: () => ({
      headline: "30 Simple Meals. One Slim Kitchen.",
      tagline: "A no-nonsense recipe plan for folks who'd rather cook than count macros. Instant download.",
      free_gift_title: "FREE: 3 Slim Kitchen Starter Recipes",
      free_gift_description: "Three meals to start with: a breakfast, a quick lunch, and a 20-minute dinner. Print, taped to the fridge, done.",
      free_gift_cta: "Send Me The Recipes",
      upsell_title: "The Simple Slim Kitchen — Complete Plan",
      upsell_description: "30 meals · 7-day grocery list · 4-week dinner rotation · Instant digital download.",
      upsell_price: 0,
      upsell_cta_label: "Get The Complete Plan — My Link",
      upsell_cta_url: "https://hop.clickbank.net/?affiliate=MICHAELBOYD&vendor=slimkitchn",
    }),
  },
  {
    id: "piurify",
    name: "PIURIFY — Hydrogen Water Bottle",
    blurb: "Piurify hydrogen-water bottle starter. Example copy loaded.",
    accent: "#287F8E",
    preview: "/templates/products/piurify.png",
    apply: () => ({
      headline: "Better Water In 3 Minutes.",
      tagline: "Piurify turns regular tap water into hydrogen-rich water with a button press. USB-charge. Carry it anywhere.",
      free_gift_title: "FREE: Why Hydrogen Water? — A Short Guide",
      free_gift_description: "A plain-English explainer of what hydrogen water is, what the studies say, and what it can't do. No hype.",
      free_gift_cta: "Send Me The Guide",
      upsell_title: "Piurify Hydrogen Water Bottle",
      upsell_description: "Portable hydrogen-water bottle. USB-rechargeable. 3-minute cycle. Glass and food-grade silicone construction.",
      upsell_price: 0,
      upsell_cta_label: "Shop Piurify — My Link",
      upsell_cta_url: "https://piurify.com/?ref=MICHAELBOYD",
    }),
  },
  {
    id: "celsius-herbs",
    name: "CELSIUS ORGANIC HERBS — Tea & Loose Leaf",
    blurb: "Certified organic teas funnel starter via Awin.",
    accent: "#2E9F3A",
    preview: "/templates/products/celsius-herbs.png",
    apply: () => ({
      headline: "Real Tea. Real Herbs. Real Quiet Mornings.",
      tagline: "Celsius Organic Herbs ships USDA-certified-organic loose-leaf tea and dried herbs to your door.",
      free_gift_title: "FREE: 3-Cup Daily Tea Ritual",
      free_gift_description: "A short, simple morning-noon-night tea routine. One quiet cup at a time. Recipe card you can print and tape on the fridge.",
      free_gift_cta: "Send Me The Recipe",
      upsell_title: "Celsius Organic Herbs Tea Collection",
      upsell_description: "USDA-certified-organic loose-leaf tea and dried herbs. Hand-packed. Free shipping over $50.",
      upsell_price: 0,
      upsell_cta_label: "Shop Celsius — My Link",
      upsell_cta_url: "https://www.awin1.com/cread.php?awinmid=126887&awinaffid=2924149&ued=https%3A%2F%2Fcelsiusherbs.com%2F",
    }),
  },

  {
    id: "sunhome-sauna",
    name: "SUN HOME ECLIPSE — Red-Light Sauna",
    blurb: "2-person red-light + infrared home sauna. Premium ticket.",
    accent: "#8B2A2A",
    preview: "/templates/products/sunhome.png",
    apply: () => ({
      headline: "A Spa Treatment That Lives In Your House.",
      tagline: "Sun Home Eclipse — a 2-person infrared and red-light sauna built for the home. Cedar interior. Lifetime warranty on the heaters.",
      free_gift_title: "FREE: The Beginner's Sauna Schedule",
      free_gift_description: "How often. How long. What to do before and after. A starter schedule for folks who've never owned a sauna.",
      free_gift_cta: "Send Me The Schedule",
      upsell_title: "Sun Home Eclipse 2-Person Sauna",
      upsell_description: "2-person infrared + red-light home sauna. Cedar interior. Pre-wired. Ships in custom crate. Financing available.",
      upsell_price: 0,
      upsell_cta_label: "See The Eclipse Sauna — My Link",
      upsell_cta_url: "https://sunhome.pxf.io/aNjejj",
    }),
  },

  /* ════════ FREE FLIPBOOK GIVE-AWAY TEMPLATES ════════
     Flipbook NAMES are real products Mike has built or is building.
     Everything else (headline, story, description, paired-product price)
     stays BLANK — Mike writes his own honest words. */
  {
    id: "flipbook-treadmill",
    name: "FLIPBOOK · 10 Minutes A Day",
    blurb: "Free flipbook lead magnet — pulls emails into your list.",
    accent: "#287F8E",
    preview: "/templates/products/flipbook-10min.png",
    apply: () => ({
      headline: "10 Minutes A Day — Free.",
      tagline: "A short flipbook for folks who haven't moved in a while. Ten minutes is enough to start.",
      free_gift_title: "10 Minutes A Day (Free Flipbook)",
      free_gift_description: "Two weeks of 10-minute routines you can do in your living room. No equipment. No gym. Read it on your phone.",
      free_gift_cta: "Send Me The Flipbook",
      upsell_title: "",
      upsell_description: "",
      upsell_price: 0,
      upsell_cta_label: "",
      upsell_cta_url: "",
    }),
  },
  {
    id: "flipbook-sugar",
    name: "FLIPBOOK · The Sugar Reset",
    blurb: "Free flipbook lead magnet — pulls emails into your list.",
    accent: "#C0392B",
    preview: "/templates/products/flipbook-sugar.png",
    apply: () => ({
      headline: "The Sugar Reset — Free.",
      tagline: "A short flipbook that walks you through the first 7 days off the white stuff. Plain English. No starvation.",
      free_gift_title: "The Sugar Reset (Free Flipbook)",
      free_gift_description: "Seven days. One small swap each day. Read it in under an hour. Free, no card required.",
      free_gift_cta: "Send Me The Flipbook",
      upsell_title: "",
      upsell_description: "",
      upsell_price: 0,
      upsell_cta_label: "",
      upsell_cta_url: "",
    }),
  },
  {
    id: "flipbook-lose10",
    name: "FLIPBOOK · Lose 10 Pounds After 60",
    blurb: "Free flipbook lead magnet — pulls emails into your list.",
    accent: "#F39A1F",
    preview: "/templates/products/flipbook-lose10.png",
    apply: () => ({
      headline: "Lose 10 Pounds After 60 — Free.",
      tagline: "A simple flipbook for folks who are tired of the gym, the apps, and the lectures. Read it on your phone.",
      free_gift_title: "Lose 10 Pounds After 60 (Free Flipbook)",
      free_gift_description: "Three small habits. Two weeks of breakfasts. One honest weigh-in plan. Free download.",
      free_gift_cta: "Send Me The Flipbook",
      upsell_title: "",
      upsell_description: "",
      upsell_price: 0,
      upsell_cta_label: "",
      upsell_cta_url: "",
    }),
  },
  {
    id: "flipbook-sleep",
    name: "FLIPBOOK · Sleep Well After 60",
    blurb: "Free flipbook lead magnet — pulls emails into your list.",
    accent: "#0F172A",
    preview: "/templates/products/flipbook-sleep.png",
    apply: () => ({
      headline: "Sleep Well After 60 — Free.",
      tagline: "A short flipbook with the night-time routine, breathing exercise, and bedroom checklist that works for older sleepers.",
      free_gift_title: "Sleep Well After 60 (Free Flipbook)",
      free_gift_description: "What to do at 8 PM. What to avoid at 9 PM. The 4-7-8 breath that actually works. Free, no card.",
      free_gift_cta: "Send Me The Flipbook",
      upsell_title: "",
      upsell_description: "",
      upsell_price: 0,
      upsell_cta_label: "",
      upsell_cta_url: "",
    }),
  },
];

export default function MirrorSiteTab() {
  const [m, setM] = useState({
    handle: "",
    headline: "",
    tagline: "",
    testimony: "",
    photo_url: "",
    enabled: true,
    affiliate_code: "",
    full_name: "",
    share_url: "",
    product_image_url: "",
    free_gift_title: "",
    free_gift_description: "",
    free_gift_cta: "",
    upsell_title: "",
    upsell_description: "",
    upsell_price: 0,
    upsell_cta_label: "",
    upsell_cta_url: "",
    // A/B Variant B
    ab_enabled: false,
    variant_b_headline: "",
    variant_b_tagline: "",
    variant_b_photo_url: "",
    variant_b_upsell_title: "",
    // Order bump
    bump_enabled: false,
    bump_image_url: "",
    bump_headline: "",
    bump_pitch: "",
    bump_check_text: "Yes — add this too",
    bump_price: 0,
    bump_cta_url: "",
    // Capture-Page Designer
    layout: "hero_side_form",
    bullets: [],
    video_url: "",
    lead_pdf_url: "",
    bg_color: "#0F1A2E",
    accent_color: "#F39A1F",
    headline_color: "#E0F2FE",
    headline_size: "2xl",
    headline_font: "display",
    subhead_color: "#FFFFFF",
    subhead_size: "lg",
    form_enabled: false,
    form_button_label: "Get Instant Access",
    form_collect_phone: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [origin, setOrigin] = useState("");

  useEffect(() => {
    setOrigin(window.location.origin);
    api.get("/mirror/me")
      .then((r) => setM(r.data))
      .catch(() => toast.error("Couldn't load your mirror site settings."))
      .finally(() => setLoading(false));
  }, []);

  const update = (k, v) => { setM((p) => ({ ...p, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/mirror/me", {
        handle: m.handle, headline: m.headline, tagline: m.tagline,
        testimony: m.testimony, photo_url: m.photo_url, enabled: m.enabled,
        product_image_url: m.product_image_url,
        free_gift_title: m.free_gift_title,
        free_gift_description: m.free_gift_description,
        free_gift_cta: m.free_gift_cta,
        upsell_title: m.upsell_title,
        upsell_description: m.upsell_description,
        upsell_price: Number(m.upsell_price) || 0,
        upsell_cta_label: m.upsell_cta_label,
        upsell_cta_url: m.upsell_cta_url,
        ab_enabled: !!m.ab_enabled,
        variant_b_headline: m.variant_b_headline,
        variant_b_tagline: m.variant_b_tagline,
        variant_b_photo_url: m.variant_b_photo_url,
        variant_b_upsell_title: m.variant_b_upsell_title,
        bump_enabled: !!m.bump_enabled,
        bump_image_url: m.bump_image_url,
        bump_headline: m.bump_headline,
        bump_pitch: m.bump_pitch,
        bump_check_text: m.bump_check_text,
        bump_price: Number(m.bump_price) || 0,
        bump_cta_url: m.bump_cta_url,
        layout: m.layout,
        bullets: (m.bullets || []).filter(Boolean),
        video_url: m.video_url,
        lead_pdf_url: m.lead_pdf_url,
        bg_color: m.bg_color,
        accent_color: m.accent_color,
        headline_color: m.headline_color,
        headline_size: m.headline_size,
        headline_font: m.headline_font,
        subhead_color: m.subhead_color,
        subhead_size: m.subhead_size,
        form_enabled: !!m.form_enabled,
        form_button_label: m.form_button_label,
        form_collect_phone: !!m.form_collect_phone,
      });
      setM(r.data);
      setDirty(false);
      toast.success("★ Saved. Your mirror site is updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save.");
    } finally { setSaving(false); }
  };

  const fullUrl = `${origin}/m/${m.handle || "your-handle"}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("★ Link copied — paste it on your business cards!");
    } catch (_e) {
      toast.error("Copy failed — long-press the URL to copy by hand.");
    }
  };

  if (loading) {
    return <div className="text-sm italic text-foreground/60">Loading your mirror site…</div>;
  }

  return (
    <div className="space-y-5 max-w-4xl" data-testid="mirror-tab">

      {/* ───── PLAIN-ENGLISH "WHAT IS THIS?" BOX ───── */}
      <div className="rounded-2xl p-5 md:p-6 shadow-md bg-white"
           style={{ border: `2px solid #2E9F3A` }}
           data-testid="funnel-builder-what-is-this">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 shadow text-white"
               style={{ background: "#2E9F3A" }}>
            <HelpCircle className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-1" style={{ color: "#2E9F3A" }}>
              ★ FUNNEL TEMPLATES · Builder
            </div>
            <h1 className="font-serif-display text-2xl md:text-3xl uppercase leading-tight mb-2" style={{ color: "#0F172A" }}>
              You Are In The Funnel Builder.
            </h1>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "#0F172A" }}>
              <strong>1.</strong> Pick a starter below (the tiles).
              {" "}<strong>2.</strong> Tweak the words and upload your photo.
              {" "}<strong>3.</strong> Hit Save — your page goes live at <span className="font-mono">/m/{m.handle || "your-name"}</span>. Share it. Every sale credits back to you.
            </p>
          </div>
        </div>
      </div>

      {/* ───── STARTER TEMPLATES — image-tile picker, top of page ───── */}
      <div className="rounded-2xl bg-white border-2 shadow-md p-5 md:p-6"
           style={{ borderColor: GOLD }} data-testid="mirror-starter-templates">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: GOLD }}>
            <FileText className="w-5 h-5" style={{ color: NAVY }}/>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: GOLD }}>
              ★ STEP 1 · PICK YOUR STARTER
            </div>
            <h2 className="font-serif-display text-2xl uppercase leading-tight" style={{ color: WINE }}>
              One Click. Fills The Whole Page.
            </h2>
            <p className="text-sm text-foreground/75 mt-0.5">
              Pick a starter and the headline, free gift, and upsell load with ready-made copy. Tweak it. Save.
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {STARTER_TEMPLATES.map((t) => (
            <button key={t.id} type="button"
                    data-testid={`mirror-template-${t.id}`}
                    onClick={() => {
                      const applied = t.apply();
                      setM((p) => ({ ...p, ...applied }));
                      setDirty(true);
                      toast.success(`★ Loaded "${t.name}" — tweak it and save.`);
                      window.scrollBy({ top: 220, behavior: "smooth" });
                    }}
                    className="text-left rounded-xl bg-white border-2 overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition group"
                    style={{ borderColor: `${t.accent}55` }}>
              {/* Big picture preview — full image, no cropping. When no preview
                  image exists we show a clean colored block with the product
                  name so members never see fake placeholder photos. */}
              <div className="relative w-full overflow-hidden flex items-center justify-center px-4"
                   style={{ aspectRatio: "4/5", background: t.preview ? `${t.accent}11` : t.accent }}>
                {t.preview ? (
                  <img src={t.preview} alt={t.name}
                       loading="lazy"
                       className="absolute inset-0 w-full h-full group-hover:scale-[1.02] transition"
                       style={{ objectFit: "contain", objectPosition: "center" }}/>
                ) : (
                  <div className="text-center px-2">
                    <div className="font-serif-display text-base md:text-lg leading-tight uppercase"
                         style={{ color: "#FFFFFF", fontWeight: 800, textShadow: "0 1px 2px rgba(0,0,0,0.18)" }}>
                      {t.name.replace(/—.+$/, "").trim()}
                    </div>
                    <div className="mt-2 inline-block px-2 py-0.5 rounded-full text-[9px] tracking-[0.22em] uppercase font-bold"
                         style={{ background: "#FFFFFF", color: t.accent }}>
                      ★ Add Your Own Photo
                    </div>
                  </div>
                )}
                <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[9px] tracking-[0.22em] uppercase font-bold shadow z-10"
                     style={{ background: "#FFFFFF", color: t.accent }}>
                  ★ STARTER
                </div>
              </div>
              <div className="p-3.5 border-t" style={{ borderColor: `${t.accent}33`, background: "#FFFFFF" }}>
                <div className="font-serif-display text-sm leading-tight uppercase mb-1"
                     style={{ color: WINE, fontWeight: 700 }}>
                  {t.name}
                </div>
                <p className="text-xs text-foreground/70 leading-snug">{t.blurb}</p>
                <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: t.accent }}>
                  Load This Template →
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* ───── HEADER + LIVE LINK ───── */}
      <div className="rounded-2xl bg-white border-2 shadow-md p-5 md:p-6"
           style={{ borderColor: O }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: O }}>
            <Sparkles className="w-5 h-5 text-white"/>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>
              ★ STEP 2 · YOUR LIVE LINK
            </div>
            <h2 className="font-serif-display text-2xl uppercase leading-tight" style={{ color: WINE }}>
              Your Logo. Your Pitch. One Link.
            </h2>
          </div>
        </div>
        <div className="rounded-lg p-3 flex flex-wrap items-center gap-2" style={{ background: "#EAF6FC", border: `1.5px solid ${O}55` }}>
          <AtSign className="w-4 h-4" style={{ color: WINE }}/>
          <span className="text-sm font-mono" style={{ color: WINE }}>{fullUrl}</span>
          <div className="ml-auto flex flex-wrap gap-2">
            <button onClick={copyLink} data-testid="mirror-copy-link"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] uppercase tracking-[0.18em] font-bold text-white"
                    style={{ background: G }}>
              <Copy className="w-3.5 h-3.5"/> Copy Link
            </button>
            <a href={`/m/${m.handle || ""}`} target="_blank" rel="noopener noreferrer"
               data-testid="mirror-preview-link"
               className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] uppercase tracking-[0.18em] font-bold border-2"
               style={{ borderColor: WINE, color: WINE, background: "#FFFFFF" }}>
              <Eye className="w-3.5 h-3.5"/> Preview
            </a>
          </div>
        </div>
      </div>

      {/* ───── STARTER TEMPLATES (legacy duplicate marker — removed) ───── */}

      {/* ───── 1. URL HANDLE ───── */}
      <Section step="1" title="Pick Your URL Username" subtitle="This is what shows up after /m/ in your link. Keep it short and easy to remember.">
        <input type="text" value={m.handle} maxLength={40}
               onChange={(e) => update("handle", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").slice(0, 40))}
               placeholder="boydfaith"
               data-testid="mirror-handle-input"
               className="w-full px-3 py-3 rounded-md text-base font-mono bg-white outline-none focus:ring-2 focus:ring-[#F39A1F]"
               style={{ border: `2px solid ${O}55`, color: WINE }}/>
        <Hint>Letters, numbers, dashes only. Other folks who already grabbed a name will block yours — pick something short and personal like your name + a word.</Hint>
      </Section>

      {/* ───── 2. CAPTURE-PAGE LAYOUT (5 designs) ───── */}
      <CapturePageDesigner m={m} update={update} />

      {/* ───── 3. YOUR IMAGE OR LOGO ───── */}
      <Section step="3" title="Your Image Or Logo" subtitle="This is what shows up in the top-left corner of your page. Upload your photo, your business logo, or pick a free stock image.">
        <ImageUploader
          testid="mirror-photo"
          imageUrl={m.photo_url}
          onChange={(url) => update("photo_url", url)}
          placeholder="YOUR IMAGE OR LOGO GOES HERE"
          aspect="square"
        />
      </Section>

      {/* ───── 4. HEADLINE + TAGLINE ─────  with styling controls */}
      <Section step="4" title="Your Headline + Tagline" subtitle="The first words a visitor reads. Keep it real, keep it warm.">
        <Field label="Big headline (top of page)">
          <input type="text" value={m.headline} maxLength={160}
                 onChange={(e) => update("headline", e.target.value)}
                 placeholder={`Welcome — I'm ${(m.full_name || "").split(" ")[0] || "your name"}.`}
                 data-testid="mirror-headline-input"
                 className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#F39A1F]"
                 style={{ border: `2px solid ${O}55`, color: WINE }}/>
        </Field>
        <HeadlineStyleRow m={m} update={update} which="headline"/>
        <Field label="Tagline (one short sentence)">
          <input type="text" value={m.tagline} maxLength={200}
                 onChange={(e) => update("tagline", e.target.value)}
                 placeholder="Faith, health, and a little bit of hope — every day."
                 data-testid="mirror-tagline-input"
                 className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#F39A1F]"
                 style={{ border: `2px solid ${O}55`, color: WINE }}/>
        </Field>
      </Section>

      {/* ───── 4. PRODUCT IMAGE ───── */}
      <Section step="4" title="Product Image" subtitle="A picture of what you're promoting — book cover, supplement bottle, course screenshot. Upload one, or grab a free stock photo.">
        <ImageUploader
          testid="mirror-product"
          imageUrl={m.product_image_url}
          onChange={(url) => update("product_image_url", url)}
          placeholder="PRODUCT IMAGE GOES HERE"
          aspect="landscape"
        />
      </Section>

      {/* ───── 5. FREE GIFT (lead capture) ───── */}
      <Section step="5"
               title="Free Gift Offer"
               subtitle="What you'll give visitors for free in exchange for their email. The free gift catches more leads than the paid offer."
               icon={Gift}
               accent={G}>
        <Field label="Free gift name">
          <input type="text" value={m.free_gift_title} maxLength={160}
                 onChange={(e) => update("free_gift_title", e.target.value)}
                 placeholder="My free 4-week devotional"
                 data-testid="mirror-gift-title"
                 className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#2E9F3A]"
                 style={{ border: `2px solid ${G}55`, color: WINE }}/>
        </Field>
        <Field label="What's in it (1-2 short sentences)">
          <textarea value={m.free_gift_description} maxLength={1000} rows={3}
                    onChange={(e) => update("free_gift_description", e.target.value)}
                    placeholder="28 mornings, 8 minutes each. One scripture, one prayer, one truth. No spam — ever."
                    data-testid="mirror-gift-desc"
                    className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#2E9F3A]"
                    style={{ border: `2px solid ${G}55`, color: WINE }}/>
        </Field>
        <Field label="Button text">
          <input type="text" value={m.free_gift_cta} maxLength={60}
                 onChange={(e) => update("free_gift_cta", e.target.value)}
                 placeholder="Send Me The Devotional"
                 data-testid="mirror-gift-cta"
                 className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#2E9F3A]"
                 style={{ border: `2px solid ${G}55`, color: WINE }}/>
        </Field>
      </Section>

      {/* ───── 6. UPSELL (the paid product) ───── */}
      <Section step="6"
               title="Paid Offer (Upsell)"
               subtitle="After they get the free gift, here's what they buy. This is where the money lands."
               icon={ShoppingBag}
               accent={WINE}>
        <Field label="Product title">
          <input type="text" value={m.upsell_title} maxLength={160}
                 onChange={(e) => update("upsell_title", e.target.value)}
                 placeholder="Get the full book — With Faith All Things Are Possible"
                 data-testid="mirror-upsell-title"
                 className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#0F172A]"
                 style={{ border: `2px solid ${WINE}55`, color: WINE }}/>
        </Field>
        <Field label="One short pitch paragraph">
          <textarea value={m.upsell_description} maxLength={1000} rows={3}
                    onChange={(e) => update("upsell_description", e.target.value)}
                    placeholder="20 chapters, plain-English Mike Boyd. Audiobook AND the 90-day course free with every paperback."
                    data-testid="mirror-upsell-desc"
                    className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#0F172A]"
                    style={{ border: `2px solid ${WINE}55`, color: WINE }}/>
        </Field>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Price (USD)">
            <input type="number" min="0" step="0.01" value={m.upsell_price || ""}
                   onChange={(e) => update("upsell_price", e.target.value)}
                   placeholder="19.95"
                   data-testid="mirror-upsell-price"
                   className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#0F172A]"
                   style={{ border: `2px solid ${WINE}55`, color: WINE }}/>
          </Field>
          <Field label="Button text">
            <input type="text" value={m.upsell_cta_label} maxLength={60}
                   onChange={(e) => update("upsell_cta_label", e.target.value)}
                   placeholder="Get The Book"
                   data-testid="mirror-upsell-cta"
                   className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#0F172A]"
                   style={{ border: `2px solid ${WINE}55`, color: WINE }}/>
          </Field>
          <Field label="Where the button goes">
            <input type="text" value={m.upsell_cta_url}
                   onChange={(e) => update("upsell_cta_url", e.target.value)}
                   placeholder="https://faitharama.com/with-faith"
                   data-testid="mirror-upsell-url"
                   className="w-full px-3 py-3 rounded-md text-sm font-mono bg-white outline-none focus:ring-2 focus:ring-[#0F172A]"
                   style={{ border: `2px solid ${WINE}55`, color: WINE }}/>
          </Field>
        </div>
        <Hint>Paste your affiliate URL or one of your funnels. Every click is tracked back to you.</Hint>
      </Section>

      {/* ────────── ORDER BUMP (small cross-sell beside main CTA) ────────── */}
      <Section title="Order Bump (Optional)" subtitle="A small cross-sell card next to your main button — folks often grab both at once. Adds 30%+ to your average order.">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!m.bump_enabled}
                 onChange={(e) => update("bump_enabled", e.target.checked)}
                 data-testid="mirror-bump-enabled"
                 className="w-5 h-5"/>
          <span className="text-sm font-bold" style={{ color: WINE }}>Turn the Order Bump on</span>
        </label>
        {m.bump_enabled && (
          <>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <Field label="Bump image URL">
                <input type="text" value={m.bump_image_url || ""} onChange={(e) => update("bump_image_url", e.target.value)}
                       placeholder="https://…/goli-bottle.png" data-testid="mirror-bump-image"
                       className="w-full px-3 py-3 rounded-md text-sm font-mono bg-white outline-none focus:ring-2 focus:ring-[#0F172A]"
                       style={{ border: `2px solid ${WINE}55`, color: WINE }}/>
              </Field>
              <Field label="Headline">
                <input type="text" value={m.bump_headline || ""} onChange={(e) => update("bump_headline", e.target.value)}
                       placeholder="Add Goli Gummies" maxLength={160} data-testid="mirror-bump-headline"
                       className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#0F172A]"
                       style={{ border: `2px solid ${WINE}55`, color: WINE }}/>
              </Field>
            </div>
            <Field label="One-line pitch">
              <input type="text" value={m.bump_pitch || ""} onChange={(e) => update("bump_pitch", e.target.value)}
                     placeholder="Two gummies = a shot of apple cider vinegar — without the burn." maxLength={400}
                     data-testid="mirror-bump-pitch"
                     className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#0F172A]"
                     style={{ border: `2px solid ${WINE}55`, color: WINE }}/>
            </Field>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Checkbox text">
                <input type="text" value={m.bump_check_text || ""} onChange={(e) => update("bump_check_text", e.target.value)}
                       placeholder="Yes — add this too" data-testid="mirror-bump-check"
                       className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#0F172A]"
                       style={{ border: `2px solid ${WINE}55`, color: WINE }}/>
              </Field>
              <Field label="Price">
                <input type="number" step="0.01" value={m.bump_price || ""} onChange={(e) => update("bump_price", e.target.value)}
                       placeholder="10.00" data-testid="mirror-bump-price"
                       className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#0F172A]"
                       style={{ border: `2px solid ${WINE}55`, color: WINE }}/>
              </Field>
              <Field label="URL when clicked">
                <input type="text" value={m.bump_cta_url || ""} onChange={(e) => update("bump_cta_url", e.target.value)}
                       placeholder="https://shopgoli.com/faitharama" data-testid="mirror-bump-url"
                       className="w-full px-3 py-3 rounded-md text-sm font-mono bg-white outline-none focus:ring-2 focus:ring-[#0F172A]"
                       style={{ border: `2px solid ${WINE}55`, color: WINE }}/>
              </Field>
            </div>
          </>
        )}
      </Section>

      {/* ────────── A/B VARIANT B (run two versions of your page at once) ────────── */}
      <Section title="A/B Test (Optional)" subtitle="Run two versions of your page at the same time. Faitharama randomly shows visitors A or B and counts which one earns more clicks.">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!m.ab_enabled}
                 onChange={(e) => update("ab_enabled", e.target.checked)}
                 data-testid="mirror-ab-enabled"
                 className="w-5 h-5"/>
          <span className="text-sm font-bold" style={{ color: WINE }}>Turn A/B Testing on</span>
        </label>
        {m.ab_enabled && (
          <>
            <Hint>Leave any field blank to keep the same as Version A. Only the filled-in fields swap.</Hint>
            <Field label="Variant B — Headline">
              <input type="text" value={m.variant_b_headline || ""} onChange={(e) => update("variant_b_headline", e.target.value)}
                     placeholder="Try a different angle…" maxLength={160}
                     data-testid="mirror-vb-headline"
                     className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#0F172A]"
                     style={{ border: `2px solid ${WINE}55`, color: WINE }}/>
            </Field>
            <Field label="Variant B — Tagline">
              <input type="text" value={m.variant_b_tagline || ""} onChange={(e) => update("variant_b_tagline", e.target.value)}
                     placeholder="A different sub-line for B…" maxLength={200}
                     data-testid="mirror-vb-tagline"
                     className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#0F172A]"
                     style={{ border: `2px solid ${WINE}55`, color: WINE }}/>
            </Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Variant B — Photo URL">
                <input type="text" value={m.variant_b_photo_url || ""} onChange={(e) => update("variant_b_photo_url", e.target.value)}
                       placeholder="https://…/another-photo.jpg"
                       data-testid="mirror-vb-photo"
                       className="w-full px-3 py-3 rounded-md text-sm font-mono bg-white outline-none focus:ring-2 focus:ring-[#0F172A]"
                       style={{ border: `2px solid ${WINE}55`, color: WINE }}/>
              </Field>
              <Field label="Variant B — Upsell Title">
                <input type="text" value={m.variant_b_upsell_title || ""} onChange={(e) => update("variant_b_upsell_title", e.target.value)}
                       placeholder="Different angle for the main offer…" maxLength={160}
                       data-testid="mirror-vb-upsell-title"
                       className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#0F172A]"
                       style={{ border: `2px solid ${WINE}55`, color: WINE }}/>
              </Field>
            </div>
            <ABStats />
          </>
        )}
      </Section>

      {/* ───── 7. TESTIMONY ───── */}
      <Section step="7" title="Why You Love Faitharama" subtitle="2-3 short paragraphs. Real beats polished. Tell folks what changed for you.">
        <textarea value={m.testimony} maxLength={4000} rows={6}
                  onChange={(e) => update("testimony", e.target.value)}
                  placeholder="Faitharama gave me back my mornings. Walks with scripture, music that lifts me, and a place where I can serve other folks too..."
                  data-testid="mirror-testimony-input"
                  className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#F39A1F]"
                  style={{ border: `2px solid ${O}55`, color: WINE }}/>
        <Hint>{m.testimony?.length || 0} / 4000 characters</Hint>
      </Section>

      {/* ───── SAVE FOOTER ───── */}
      <div className="sticky bottom-3 z-10 rounded-2xl p-4 flex items-center gap-3 shadow-lg"
           style={{ background: "#FFFFFF", border: `3px solid ${G}` }}>
        <button onClick={save} disabled={!dirty || saving}
                data-testid="mirror-save"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow disabled:opacity-50"
                style={{ background: G }}>
          <Save className="w-4 h-4" /> {saving ? "Saving…" : (dirty ? "★ Save Changes" : "Saved")}
        </button>
        {dirty && <span className="text-sm italic text-foreground/65">You have unsaved changes.</span>}
        <a href={`/m/${m.handle || ""}`} target="_blank" rel="noopener noreferrer"
           className="ml-auto inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold"
           style={{ color: WINE }}>
          <ExternalLink className="w-3.5 h-3.5"/> View My Live Page
        </a>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
   Reusable Section card with the step number badge
   ────────────────────────────────────────────────────────────────── */
function Section({ step, title, subtitle, children, icon: Icon, accent }) {
  const a = accent || O;
  return (
    <div className="rounded-2xl bg-white border shadow-sm p-5 md:p-6"
         style={{ borderColor: `${a}55` }}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-white shrink-0 shadow"
             style={{ background: a }}>
          {Icon ? <Icon className="w-4 h-4"/> : step}
        </div>
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold mb-0.5" style={{ color: a }}>
            STEP {step}
          </div>
          <h3 className="font-serif-display text-xl leading-tight" style={{ color: WINE, fontWeight: 700 }}>
            {title}
          </h3>
          {subtitle && <p className="text-sm text-foreground/70 mt-1">{subtitle}</p>}
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
   Image uploader — drop-zone + URL input. Uses /api/uploads/image
   ────────────────────────────────────────────────────────────────── */
function ImageUploader({ imageUrl, onChange, placeholder, aspect = "landscape", testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [showPexels, setShowPexels] = useState(false);
  const [showAI, setShowAI] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG, PNG, GIF, WEBP).");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too big — keep it under 10 MB.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("★ Uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setBusy(false); }
  };

  const aspectClass = aspect === "square" ? "aspect-square max-w-[280px]" : "aspect-video";

  return (
    <div>
      <div
        className={`relative ${aspectClass} w-full rounded-xl border-2 border-dashed bg-[#FFFFFF] flex items-center justify-center overflow-hidden cursor-pointer transition hover:border-[#F39A1F]`}
        style={{ borderColor: imageUrl ? "transparent" : `${O}88` }}
        onClick={() => inputRef.current?.click()}
        data-testid={`${testid}-dropzone`}>
        {imageUrl ? (
          <img src={imageUrl} alt="" className="w-full h-full object-contain"/>
        ) : (
          <div className="text-center px-4">
            <ImageIcon className="w-10 h-10 mx-auto mb-2" style={{ color: `${O}AA` }}/>
            <div className="font-bold uppercase tracking-[0.22em] text-sm" style={{ color: O }}>
              {placeholder}
            </div>
            <div className="text-[11px] mt-1" style={{ color: WINE }}>
              Click here or drag a file in to upload
            </div>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(255,255,255,0.85)" }}>
            <div className="text-sm font-bold uppercase tracking-wider" style={{ color: WINE }}>Uploading…</div>
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" hidden
             data-testid={`${testid}-file`}
             onChange={(e) => upload(e.target.files?.[0])}/>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
                data-testid={`${testid}-upload-btn`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] uppercase tracking-[0.18em] font-bold text-white shadow"
                style={{ background: O }}>
          <Upload className="w-3.5 h-3.5"/> {imageUrl ? "Replace Image" : "Upload Image"}
        </button>
        <button type="button" onClick={() => setShowPexels(true)} disabled={busy}
                data-testid={`${testid}-pexels-btn`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] uppercase tracking-[0.18em] font-bold text-white shadow"
                style={{ background: "#287F8E" }}>
          <Camera className="w-3.5 h-3.5"/> Free Stock Photos
        </button>
        <button type="button" onClick={() => setShowAI(true)} disabled={busy}
                data-testid={`${testid}-ai-btn`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] uppercase tracking-[0.18em] font-bold text-white shadow"
                style={{ background: "#2E9F3A" }}>
          ★ AI Make Image
        </button>
        {imageUrl && (
          <button type="button" onClick={() => onChange("")}
                  data-testid={`${testid}-remove`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] uppercase tracking-[0.18em] font-bold border-2"
                  style={{ borderColor: WINE, color: WINE, background: "#FFFFFF" }}>
            <Trash2 className="w-3.5 h-3.5"/> Remove
          </button>
        )}
        <span className="text-[11px] italic text-foreground/55 ml-auto">
          JPG, PNG, GIF, WEBP · Up to 10 MB
        </span>
      </div>
      {showPexels && (
        <PexelsPicker
          testid={testid}
          onClose={() => setShowPexels(false)}
          onPick={(url) => { onChange(url); setShowPexels(false); toast.success("★ Picked!"); }}
        />
      )}
      {showAI && (
        <FunnelAIImagePicker
          onClose={() => setShowAI(false)}
          onPick={(url) => { onChange(url); setShowAI(false); toast.success("★ Image ready."); }}
        />
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
   AI image picker — Nano Banana via /api/images/generate.
   Same UX as in MyMirrorsTab — describe → make → use.
   ────────────────────────────────────────────────────────────────── */
function FunnelAIImagePicker({ onPick, onClose }) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  const generate = async (e) => {
    if (e) e.preventDefault();
    if (prompt.trim().length < 3) { toast.error("Tell it what to make."); return; }
    setBusy(true);
    setImageUrl("");
    try {
      const { data } = await api.post("/images/generate", { prompt: prompt.trim() }, { timeout: 90000 });
      setImageUrl(data?.url || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't make the image.");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="funnel-ai-picker">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center gap-3" style={{ background: "#EAF6FC" }}>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold flex-1" style={{ color: "#2E9F3A" }}>★ MAKE A PRODUCT IMAGE</div>
          <button onClick={onClose} className="w-8 h-8 rounded flex items-center justify-center" style={{ background: "#0F172A", color: "#FFFFFF" }}>×</button>
        </div>
        <div className="p-5 flex-1 overflow-y-auto">
          <form onSubmit={generate}>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: "#0F172A" }}>
              Describe the image you want
            </label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
                      placeholder='e.g. "Clean white bottle of apple cider vinegar gummies on a sunlit marble counter, soft natural light, no text on the label"'
                      data-testid="funnel-ai-prompt"
                      className="w-full px-3 py-2 rounded border-2 text-sm outline-none focus:ring-2 focus:ring-[#F39A1F]"
                      style={{ borderColor: "#E5E7EB" }}/>
            <button type="submit" disabled={busy}
                    data-testid="funnel-ai-go"
                    className="mt-3 w-full px-5 py-3 rounded text-sm font-bold uppercase tracking-[0.18em] text-white disabled:opacity-50"
                    style={{ background: "#2E9F3A" }}>
              {busy ? "Making it (~20 sec)…" : "★ Make This Image"}
            </button>
          </form>
          {imageUrl && (
            <div className="mt-5">
              <div className="rounded overflow-hidden border-2 mb-3" style={{ borderColor: "#F39A1F" }}>
                <img src={imageUrl.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${imageUrl}` : imageUrl} alt="" className="w-full"/>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => onPick(imageUrl)}
                        data-testid="funnel-ai-use"
                        className="flex-1 px-5 py-3 rounded text-sm font-bold uppercase tracking-[0.18em] text-white"
                        style={{ background: "#2E9F3A" }}>
                  Use This Image
                </button>
                <button type="button" onClick={() => generate()}
                        className="px-5 py-3 rounded text-sm font-bold uppercase tracking-[0.18em] border-2"
                        style={{ borderColor: "#287F8E", color: "#287F8E", background: "#FFFFFF" }}>
                  Try Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
   Pexels Picker — small modal that searches Pexels stock photos and
   lets the member pick one with a single click. Uses /api/photos/search.
   ────────────────────────────────────────────────────────────────── */
function PexelsPicker({ onClose, onPick, testid }) {
  const [q, setQ] = useState("country church");
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  const run = async (query) => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get("/photos/search", { params: { q: query, page: 1 } });
      setPhotos(data?.photos || []);
    } catch (_err) {
      toast.error("Couldn't reach Pexels right now.");
    } finally { setLoading(false); }
  };

  useEffect(() => { run(q); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const quick = ["country church", "praying hands", "open Bible", "grandmother",
                 "sunrise field", "family dinner", "front porch", "Sunday morning"];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3"
         style={{ background: "rgba(15,26,46,0.78)" }}
         data-testid={`${testid}-pexels-modal`}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rounded-2xl bg-white max-w-3xl w-full max-h-[88vh] overflow-hidden flex flex-col shadow-2xl"
           style={{ border: `3px solid ${O}` }}>
        {/* Header */}
        <div className="px-5 py-3 flex items-center gap-2" style={{ background: "#D9EEFB", borderBottom: `2px solid ${O}55` }}>
          <Camera className="w-5 h-5" style={{ color: WINE }}/>
          <h3 className="font-serif-display text-xl uppercase flex-1" style={{ color: WINE }}>
            Free Stock Photos
          </h3>
          <button type="button" onClick={onClose}
                  data-testid={`${testid}-pexels-close`}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-black/10">
            <X className="w-5 h-5" style={{ color: WINE }}/>
          </button>
        </div>

        {/* Search bar */}
        <div className="px-5 py-3 flex flex-wrap items-center gap-2" style={{ borderBottom: `1px solid ${O}33` }}>
          <div className="flex-1 min-w-[200px] flex items-center gap-2 px-3 py-2 rounded-md"
               style={{ background: "#FFFFFF", border: `2px solid ${O}55` }}>
            <Search className="w-4 h-4" style={{ color: WINE }}/>
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && run(q)}
                   data-testid={`${testid}-pexels-input`}
                   placeholder="Search for any photo…"
                   className="flex-1 outline-none bg-transparent text-base"
                   style={{ color: WINE }}/>
          </div>
          <button type="button" onClick={() => run(q)} disabled={loading}
                  data-testid={`${testid}-pexels-search`}
                  className="px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.18em] font-bold text-white shadow disabled:opacity-50"
                  style={{ background: G }}>
            {loading ? "Searching…" : "Search"}
          </button>
        </div>

        {/* Quick chips */}
        <div className="px-5 pt-2 pb-1 flex flex-wrap gap-1.5">
          {quick.map((s) => (
            <button key={s} type="button"
                    onClick={() => { setQ(s); run(s); }}
                    className="px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold hover:scale-[1.03] transition"
                    style={{ background: "#EAF6FC", color: WINE, border: `1.5px solid ${O}55` }}>
              {s}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && photos.length === 0 ? (
            <div className="py-10 text-center inline-flex items-center gap-2 w-full justify-center text-sm italic" style={{ color: WINE }}>
              <Loader2 className="w-4 h-4 animate-spin"/> Looking for photos…
            </div>
          ) : photos.length === 0 ? (
            <p className="py-10 text-center italic text-sm text-foreground/60">No photos found. Try another search.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {photos.map((p) => (
                <button key={p.id} type="button"
                        onClick={() => onPick(p.medium || p.large || p.src)}
                        data-testid={`${testid}-pexels-photo-${p.id}`}
                        className="block rounded-lg overflow-hidden border-2 hover:shadow-xl hover:scale-[1.02] transition aspect-[4/3] bg-white"
                        style={{ borderColor: `${O}55` }}>
                  <img src={p.medium || p.large || p.src} alt={p.alt || "Pexels stock photo"}
                       loading="lazy"
                       className="w-full h-full object-cover"/>
                </button>
              ))}
            </div>
          )}
          <p className="mt-4 text-[10px] italic text-center text-foreground/50">
            Photos via Pexels — free for personal &amp; commercial use.
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.18em] uppercase font-bold mb-1.5" style={{ color: WINE }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function Hint({ children }) {
  return <div className="text-[11px] italic text-foreground/60 mt-1.5">{children}</div>;
}

/* Small A/B stats card — loads view & click counts for both variants. */
function ABStats() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/mirror/me/stats")
       .then((r) => setS(r.data?.stats))
       .catch(() => {});
  }, []);
  if (!s) return null;
  const pct = (n, d) => d > 0 ? ((n / d) * 100).toFixed(1) + "%" : "—";
  const row = (label, k) => (
    <tr className="border-t" style={{ borderColor: "#E5E1D6" }}>
      <td className="py-2 px-3 font-bold" style={{ color: WINE }}>{label}</td>
      <td className="py-2 px-3 text-center">{s.a[k]}</td>
      <td className="py-2 px-3 text-center">{s.b[k]}</td>
    </tr>
  );
  return (
    <div className="mt-4 rounded-lg border-2 bg-white overflow-hidden" style={{ borderColor: WINE }} data-testid="mirror-ab-stats">
      <div className="px-3 py-2 text-[10px] tracking-[0.22em] uppercase font-bold" style={{ background: WINE, color: "#FFFFFF" }}>
        ★ How A vs B Are Doing
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr style={{ background: "#FFFFFF" }}>
            <th className="py-2 px-3 text-left" style={{ color: WINE }}>Metric</th>
            <th className="py-2 px-3 text-center" style={{ color: WINE }}>Version A</th>
            <th className="py-2 px-3 text-center" style={{ color: WINE }}>Version B</th>
          </tr>
        </thead>
        <tbody>
          {row("Views", "view")}
          {row("Main button clicks", "cta")}
          {row("Order Bump clicks", "bump")}
          <tr className="border-t" style={{ borderColor: "#E5E1D6", background: "#F4F0E6" }}>
            <td className="py-2 px-3 font-bold" style={{ color: WINE }}>Click-Thru</td>
            <td className="py-2 px-3 text-center font-bold" style={{ color: WINE }}>{pct(s.a.cta, s.a.view)}</td>
            <td className="py-2 px-3 text-center font-bold" style={{ color: WINE }}>{pct(s.b.cta, s.b.view)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}


/* ════════════════════════════════════════════════════════════════════════
   CAPTURE-PAGE DESIGNER — pick a layout + customize colors / sizes /
   media / form / lead-magnet. Drives MirrorSite.jsx layout switch.
   ════════════════════════════════════════════════════════════════════════ */
/* 5 COLOR VARIATIONS of the SAME funnel — Hero + Side Form.
   Mike's call (Jun 2026): seniors get confused by multiple layouts.
   ONE funnel, 5 colors. That's it. */
const LAYOUTS = [
  { id: "trust",   name: "Trust (Navy + Gold)",      bg: "#0F1A2E", accent: "#E8B539", headline: "#E0F2FE", subhead: "#FFFFFF" },
  { id: "power",   name: "Power (Wine + Cream)",     bg: "#0F172A", accent: "#F39A1F", headline: "#E0F2FE", subhead: "#FFFFFF" },
  { id: "health",  name: "Health (Forest + Cream)",  bg: "#1F4D2A", accent: "#E8B539", headline: "#FFFFFF", subhead: "#FFFFFF" },
  { id: "clean",   name: "Clean (White + Orange)",   bg: "#FFFFFF", accent: "#F39A1F", headline: "#0F172A", subhead: "#3A3A3A" },
  { id: "sunrise", name: "Sunrise (Cream + Orange)", bg: "#FFFFFF", accent: "#C0392B", headline: "#0F172A", subhead: "#0F172A" },
];

const COLOR_PRESETS = [
  "#0F1A2E", "#0F172A", "#0F172A", "#1F4D2A", "#2E1F5B",
  "#0F172A", "#FFFFFF", "#FFFFFF", "#F39A1F", "#C0392B",
  "#E8B539", "#E0F2FE", "#2E9F3A", "#287F8E",
];

/* 5 ONE-CLICK COLOR THEMES — Mike picked these (Jun 2026).
   Click one and the page background + accent + headline color all change
   together. Member can still fine-tune any single color afterward. */
const COLOR_THEMES = [
  { id: "trust",   name: "Trust",    blurb: "Navy + Gold — looks professional",
    bg: "#0F1A2E", accent: "#E8B539", headline: "#E0F2FE", subhead: "#FFFFFF" },
  { id: "power",   name: "Power",    blurb: "Wine + Cream — Faitharama brand",
    bg: "#0F172A", accent: "#F39A1F", headline: "#E0F2FE", subhead: "#FFFFFF" },
  { id: "health",  name: "Health",   blurb: "Forest + Cream — wellness vibe",
    bg: "#1F4D2A", accent: "#E8B539", headline: "#FFFFFF", subhead: "#FFFFFF" },
  { id: "clean",   name: "Clean",    blurb: "White + Orange — LiveGood style",
    bg: "#FFFFFF", accent: "#F39A1F", headline: "#0F172A", subhead: "#3A3A3A" },
  { id: "sunrise", name: "Sunrise",  blurb: "Cream + Burnt Orange — warm & friendly",
    bg: "#FFFFFF", accent: "#C0392B", headline: "#0F172A", subhead: "#0F172A" },
];

const SIZE_OPTS = [
  { id: "lg",   label: "Big" },
  { id: "xl",   label: "Bigger" },
  { id: "2xl",  label: "Even Bigger" },
  { id: "3xl",  label: "HUGE" },
  { id: "huge", label: "MASSIVE" },
];

const FONT_OPTS = [
  { id: "display", label: "Display (Cormorant)" },
  { id: "serif",   label: "Serif (Georgia)" },
  { id: "sans",    label: "Sans (Inter)" },
];

function CapturePageDesigner({ m, update }) {
  const bullets = Array.isArray(m.bullets) ? m.bullets : [];
  const setBullet = (idx, val) => {
    const next = bullets.slice();
    next[idx] = val;
    update("bullets", next);
  };
  const addBullet = () => update("bullets", [...bullets, ""]);
  const removeBullet = (idx) => update("bullets", bullets.filter((_, i) => i !== idx));

  const [pdfBusy, setPdfBusy] = useState(false);
  const pdfRef = useRef(null);
  const uploadPdf = async (file) => {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) return toast.error("PDF only please.");
    if (file.size > 25 * 1024 * 1024) return toast.error("PDF over 25 MB — please shrink it.");
    setPdfBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" }});
      update("lead_pdf_url", data.url);
      toast.success("★ PDF uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't upload PDF.");
    } finally { setPdfBusy(false); }
  };

  return (
    <Section step="2" title="Pick A Funnel (5 Colors)" subtitle="Same funnel design — 5 color choices. Click one and the page goes live in those colors.">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="layout-picker">
        {LAYOUTS.map((L) => {
          const active = (m.bg_color === L.bg && m.accent_color === L.accent);
          return (
            <button key={L.id} type="button"
                    onClick={() => {
                      update("layout", "hero_side_form");
                      update("bg_color", L.bg);
                      update("accent_color", L.accent);
                      update("headline_color", L.headline);
                      update("subhead_color", L.subhead);
                      toast.success(`★ ${L.name.split(' ')[0]} colors applied`);
                    }}
                    data-testid={`layout-${L.id}`}
                    className="text-left rounded-xl overflow-hidden hover:shadow-xl transition border-2 bg-white"
                    style={{
                      borderColor: active ? G : `${O}33`,
                      boxShadow: active ? `0 0 0 3px ${G}33` : undefined,
                    }}>
              <FunnelPreview bg={L.bg} accent={L.accent} headline={L.headline}/>
              <div className="px-3 py-2.5 flex items-center justify-between gap-2 border-t" style={{ borderColor: `${O}33`, background: active ? "#E8F5E9" : "#FFFFFF" }}>
                <div className="font-serif-display text-base uppercase leading-tight" style={{ color: WINE }}>
                  {L.name}
                </div>
                {active ? (
                  <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase font-bold text-white" style={{ background: G }}>★ PICKED</span>
                ) : (
                  <span className="text-[10px] tracking-[0.18em] uppercase font-bold" style={{ color: O }}>Click →</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Colors — manual fine-tuning */}
      <div className="grid sm:grid-cols-2 gap-4 mt-3">
        <Field label="Page background color">
          <ColorPicker testid="bg-color" value={m.bg_color} onChange={(c) => update("bg_color", c)}/>
        </Field>
        <Field label="Accent color (buttons, frames)">
          <ColorPicker testid="accent-color" value={m.accent_color} onChange={(c) => update("accent_color", c)}/>
        </Field>
      </div>

      {/* Video frame */}
      <Field label="Video URL (YouTube, Vimeo, or direct .mp4)">
        <input type="text" value={m.video_url || ""} maxLength={600}
               onChange={(e) => update("video_url", e.target.value)}
               placeholder="https://youtu.be/..."
               data-testid="capture-video-url"
               className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#F39A1F]"
               style={{ border: `2px solid ${O}55`, color: WINE }}/>
        <Hint>Paste a YouTube/Vimeo link — it shows up as a real video player on your page.</Hint>
      </Field>

      {/* Bullets */}
      <Field label="Benefit bullets (3-5 short lines — what they get)">
        <div className="space-y-2" data-testid="bullets-list">
          {bullets.map((b, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-base" style={{ color: O }}>★</span>
              <input type="text" value={b} maxLength={160}
                     onChange={(e) => setBullet(i, e.target.value)}
                     placeholder="Life-Changing Products!"
                     data-testid={`bullet-${i}`}
                     className="flex-1 px-3 py-2 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#F39A1F]"
                     style={{ border: `2px solid ${O}55`, color: WINE }}/>
              <button type="button" onClick={() => removeBullet(i)}
                      data-testid={`bullet-remove-${i}`}
                      className="px-2 py-1.5 rounded-md text-xs font-bold border-2 hover:bg-red-50"
                      style={{ borderColor: "#C0392B", color: "#C0392B" }}>
                <Trash2 className="w-3.5 h-3.5"/>
              </button>
            </div>
          ))}
          {bullets.length < 8 && (
            <button type="button" onClick={addBullet}
                    data-testid="bullet-add"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] uppercase tracking-[0.18em] font-bold text-white shadow"
                    style={{ background: G }}>
              + Add A Bullet
            </button>
          )}
        </div>
      </Field>

      {/* PDF / Book lead magnet */}
      <Field label="Book or PDF to give away (lead magnet)">
        <input type="file" accept="application/pdf" hidden ref={pdfRef}
               onChange={(e) => uploadPdf(e.target.files?.[0])}
               data-testid="capture-pdf-file"/>
        {m.lead_pdf_url ? (
          <div className="flex items-center gap-2 flex-wrap">
            <a href={m.lead_pdf_url} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-bold border-2"
               style={{ borderColor: WINE, color: WINE, background: "#FFFFFF" }}>
              <FileText className="w-4 h-4"/> Open Uploaded PDF
            </a>
            <button type="button" onClick={() => pdfRef.current?.click()} disabled={pdfBusy}
                    data-testid="capture-pdf-replace"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] uppercase tracking-[0.18em] font-bold text-white shadow"
                    style={{ background: O }}>
              <Upload className="w-3.5 h-3.5"/> Replace
            </button>
            <button type="button" onClick={() => update("lead_pdf_url", "")}
                    data-testid="capture-pdf-remove"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] uppercase tracking-[0.18em] font-bold border-2"
                    style={{ borderColor: WINE, color: WINE, background: "#FFFFFF" }}>
              <Trash2 className="w-3.5 h-3.5"/> Remove
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => pdfRef.current?.click()} disabled={pdfBusy}
                  data-testid="capture-pdf-upload"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md text-[11px] uppercase tracking-[0.18em] font-bold text-white shadow"
                  style={{ background: O }}>
            <Upload className="w-3.5 h-3.5"/> {pdfBusy ? "Uploading…" : "Upload Your PDF / Book"}
          </button>
        )}
        <Hint>When someone signs up on your form, they get an email with this file. Up to 25 MB.</Hint>
      </Field>

      {/* Form on/off + button label */}
      <div className="rounded-lg p-4 mt-2" style={{ background: "#EAF6FC", border: `2px solid ${O}55` }}>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!m.form_enabled}
                 onChange={(e) => update("form_enabled", e.target.checked)}
                 data-testid="form-enabled"
                 className="w-5 h-5"/>
          <span className="text-base font-bold" style={{ color: WINE }}>
            {m.form_enabled ? "★ Sign-up form is ON — collect leads" : "Sign-up form is OFF"}
          </span>
        </label>
        {m.form_enabled && (
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            <Field label="Sign-up button label">
              <input type="text" value={m.form_button_label || ""} maxLength={60}
                     onChange={(e) => update("form_button_label", e.target.value)}
                     placeholder="Get Instant Access"
                     data-testid="form-button-label"
                     className="w-full px-3 py-2.5 rounded-md text-base bg-white outline-none focus:ring-2"
                     style={{ border: `2px solid ${O}55`, color: WINE }}/>
            </Field>
            <Field label="Collect phone number too?">
              <label className="flex items-center gap-2 cursor-pointer h-[42px]">
                <input type="checkbox" checked={!!m.form_collect_phone}
                       onChange={(e) => update("form_collect_phone", e.target.checked)}
                       data-testid="form-collect-phone"
                       className="w-5 h-5"/>
                <span className="text-sm" style={{ color: WINE }}>Add phone field</span>
              </label>
            </Field>
          </div>
        )}
      </div>
    </Section>
  );
}

/* Headline style row used under the headline / tagline fields */
function HeadlineStyleRow({ m, update, which = "headline" }) {
  const colorKey = which === "headline" ? "headline_color" : "subhead_color";
  const sizeKey  = which === "headline" ? "headline_size"  : "subhead_size";
  const fontKey  = which === "headline" ? "headline_font"  : null;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
      <Field label={`Color`}>
        <ColorPicker testid={`${which}-color`} value={m[colorKey]} onChange={(c) => update(colorKey, c)}/>
      </Field>
      <Field label={`Size`}>
        <select value={m[sizeKey] || (which === "headline" ? "2xl" : "lg")}
                onChange={(e) => update(sizeKey, e.target.value)}
                data-testid={`${which}-size`}
                className="w-full px-3 py-2.5 rounded-md text-base bg-white outline-none focus:ring-2"
                style={{ border: `2px solid ${O}55`, color: WINE }}>
          {SIZE_OPTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </Field>
      {fontKey && (
        <Field label={`Font`}>
          <select value={m[fontKey] || "display"}
                  onChange={(e) => update(fontKey, e.target.value)}
                  data-testid={`${which}-font`}
                  className="w-full px-3 py-2.5 rounded-md text-base bg-white outline-none focus:ring-2"
                  style={{ border: `2px solid ${O}55`, color: WINE }}>
            {FONT_OPTS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
        </Field>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
   FunnelPreview — ONE design (Hero + Side Form). Re-rendered in 5
   different color combos. Pure SVG, scales perfectly, loads instantly.
   ────────────────────────────────────────────────────────────────── */
function FunnelPreview({ bg = "#0F1A2E", accent = "#F39A1F", headline = "#E0F2FE" }) {
  const soft = "#FFFFFF";
  const btn  = "#C0392B";
  // Sub-text color: white when bg is dark, dark when bg is light
  const isLight = /^#?(?:f|e|d)/i.test(bg);
  const subTxt = isLight ? "#0F172A" : "#FFFFFF";
  return (
    <svg viewBox="0 0 320 180" className="block w-full aspect-video" preserveAspectRatio="xMidYMid meet">
      <rect width="320" height="180" fill={bg}/>
      <rect width="320" height="14" fill="#E0F2FE"/>
      <text x="160" y="10" textAnchor="middle" fontSize="6" fontFamily="Georgia" fontWeight="700"><tspan fill="#2E9F3A">FAITH</tspan><tspan fill="#F39A1F">ARAMA.COM</tspan></text>
      {/* photo circle */}
      <circle cx="40" cy="95" r="22" fill="#fff" stroke={accent} strokeWidth="2"/>
      <circle cx="40" cy="90" r="8" fill={isLight ? "#287F8E" : bg}/>
      <path d="M22 110 Q40 95 58 110 L58 117 L22 117 Z" fill={isLight ? "#287F8E" : bg}/>
      {/* headline */}
      <rect x="75" y="55" width="135" height="9" rx="2" fill={headline}/>
      <rect x="75" y="70" width="100" height="9" rx="2" fill={headline}/>
      <rect x="75" y="88" width="90"  height="4" rx="2" fill={subTxt} opacity="0.65"/>
      <rect x="75" y="96" width="120" height="4" rx="2" fill={subTxt} opacity="0.5"/>
      {/* bullets */}
      <circle cx="78" cy="115" r="2" fill="#2E9F3A"/><rect x="84" y="113" width="60" height="3" rx="1" fill={subTxt} opacity="0.6"/>
      <circle cx="78" cy="124" r="2" fill="#2E9F3A"/><rect x="84" y="122" width="50" height="3" rx="1" fill={subTxt} opacity="0.6"/>
      <circle cx="78" cy="133" r="2" fill="#2E9F3A"/><rect x="84" y="131" width="65" height="3" rx="1" fill={subTxt} opacity="0.6"/>
      {/* form card */}
      <rect x="225" y="40" width="80" height="110" rx="4" fill="#fff" stroke={accent} strokeWidth="1.5"/>
      <rect x="225" y="40" width="80" height="20" rx="4" fill={accent}/>
      <text x="265" y="54" textAnchor="middle" fontSize="6" fill="#0F1A2E" fontWeight="700">SIGN UP</text>
      <rect x="232" y="68" width="66" height="10" rx="1.5" fill={soft}/>
      <rect x="232" y="82" width="66" height="10" rx="1.5" fill={soft}/>
      <rect x="232" y="96" width="66" height="10" rx="1.5" fill={soft}/>
      <rect x="232" y="115" width="66" height="14" rx="2" fill={btn}/>
      <text x="265" y="125" textAnchor="middle" fontSize="6" fill="#fff" fontWeight="800">GET ACCESS</text>
    </svg>
  );
}


function ColorPicker({ value, onChange, testid }) {

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testid}>
      <input type="color" value={value || "#000000"}
             onChange={(e) => onChange(e.target.value)}
             data-testid={`${testid}-input`}
             className="w-12 h-10 rounded-md cursor-pointer"
             style={{ border: `2px solid ${O}55`, padding: 2 }}/>
      <input type="text" value={value || ""} maxLength={20}
             onChange={(e) => onChange(e.target.value)}
             data-testid={`${testid}-hex`}
             placeholder="#000000"
             className="flex-1 px-2 py-2 rounded-md text-sm font-mono bg-white outline-none focus:ring-2"
             style={{ border: `2px solid ${O}55`, color: WINE, minWidth: 100 }}/>
      <div className="flex gap-1 flex-wrap">
        {COLOR_PRESETS.map((c) => (
          <button key={c} type="button"
                  onClick={() => onChange(c)}
                  data-testid={`${testid}-preset-${c}`}
                  className="w-6 h-6 rounded-sm hover:scale-110 transition border"
                  style={{ background: c, borderColor: c === "#FFFFFF" ? "#999" : c }}/>
        ))}
      </div>
    </div>
  );
}


// (duplicate ColorPicker removed)
